import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAddinToken } from '../Microsoft_Auth/Login';
import { GetWorkFlows } from '../Services/GetWorkFlows'; 
import AppLoader from '../Loader/Loader';
import { loginUrl } from '../Configs/Configs';
import { portant } from '../../theme/portantTheme';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [isPressed, setIsPressed] = useState(false);
  const [loading, setLoading] = useState(false); 
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); 
  
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "info" });

  const showToast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  // --- 1. INITIAL AUTH CHECK ---
  useEffect(() => {
    const existingToken = localStorage.getItem("addin_token");
    
    if (existingToken) {
      GetWorkFlows(existingToken, (data, error) => {
        if (data) {
          navigate("/home");
        } else {
          localStorage.removeItem("addin_token");
          setIsCheckingAuth(false);
          console.log(error);
          
        }
      });
    } else {
      setIsCheckingAuth(false);
          navigate("/");

    }
  }, [navigate]);



  // --- 2. MANUAL LOGIN HANDLER ---
  const handleLogin = async () => {
    setLoading(true); // Start loader
    showToast("Opening Microsoft login…", "info");

    Office.context.ui.displayDialogAsync(
      loginUrl,
      { height: 70, width: 40, displayInIframe: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          setLoading(false); // Stop loader if window fails to open
          showToast("Unable to open login window", "error");
          return;
        }

        const dialog = result.value;

        // Message received from Redirect.html
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          async (arg: any) => {
            try {
              const data = JSON.parse(arg.message);

              if (data.code) {
                showToast("Login successful. Verifying account…", "success");
                dialog.close(); // Close window, but keep loader running until token is swapped
                
                await getAddinToken(data.code, (token, err) => {
                  setLoading(false); // Final stop
                  if (token) {
                    showToast("Logged in successfully", "success");
                    navigate("/home");
                  }
                if (err) {
  if (err.type === "NO_ACCOUNT") {
    showToast(
      "No Portant account found. Please sign up at https://app.portant.co",
      "warning"
    );
  } else {
    showToast("Failed to complete login", "error");
  }
}
                });
              }

              if (data.error) {
                setLoading(false);
                showToast("Microsoft login failed", "error");
                dialog.close();
              }
            } catch (err) {
              setLoading(false);
              showToast("Unexpected login response", "error");
            }
          }
        );

        // Handle case where user closes the popup manually (X button)
        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (__arg: any) => {
            setLoading(false); // Stop loader if user cancels
            showToast("Login window closed", "warning");
          }
        );
      }
    );
  };

  // --- 3. UI RENDERING ---

  // Use the custom AppLoader for the initial check too for better branding
  if (isCheckingAuth) {
    return <AppLoader />;
  }

  return (
    <Box
      sx={{
        minHeight: '96vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        p: 1,
        bgcolor: portant.canvas,
      }}
    >

      {/* Show loader during login process */}
      {loading && <AppLoader />}

      <Box sx={{ textAlign: 'center', maxWidth: 340, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <img src={require('../../../../assets/portant-logo-wordmark.svg')} alt="Portant" width={170} />
        </Box>
        <Typography variant="overline" sx={{ color: portant.orange, display: 'block', mb: 1 }}>
          Microsoft 365 add-in
        </Typography>
        <Typography variant="h4" gutterBottom sx={{ px: 2 }}>
          Document automation, inside Word and PowerPoint
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, px: 2, lineHeight: 1.6 }}>
          Sign in with your Microsoft 365 account to bring your Portant workflows and merge tags straight into Word and PowerPoint.
        </Typography>

        <Stack
          spacing={2}
          sx={{ cursor: "pointer", display: "inline-block", width: "fit-content" }}
        >
          <img
            src={require('../../../../assets/MS_login_button.png')}
            alt="MS Button"
            width={250}
            style={{
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              transform: isPressed ? "scale(0.95)" : "scale(1)",
              boxShadow: isPressed ? "inset 0 2px 5px rgba(0,0,0,0.3)" : "0 4px 10px rgba(0,0,0,0.2)",
              borderRadius: "8px",
            }}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onMouseLeave={() => setIsPressed(false)}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
            onClick={handleLogin}
          />
        </Stack>
      </Box>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={handleCloseToast} 
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
          {toast.message.includes("https://") ? (
  <>
    No Portant account found. Please{" "}
    <a
      href="https://app.portant.co"
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#fff", textDecoration: "underline" }}
    >
      sign up here
    </a>
    .
  </>
) : (
  toast.message
)}

        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;